import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";

// ============================================================================
// Types
// ============================================================================

export type DocumentEstado =
  | "PENDIENTE"
  | "EXTRAYENDO_TEXTO"
  | "GENERANDO_CHUNKS"
  | "GENERANDO_CONTEXTO"
  | "GENERANDO_EMBEDDINGS"
  | "COMPLETADO"
  | "ERROR"
  | "DUPLICADO";

export type SourceKind =
  | "pdf"
  | "docx"
  | "xlsx"
  | "email_eml"
  | "email_msg"
  | "email_txt"
  | "txt"
  | "attachment";

export interface DocumentEmailMeta {
  sender_email: string | null;
  date: string | null;
  subject: string | null;
}

export interface DocumentOut {
  id: string;
  filename: string;
  source_kind: SourceKind | null;
  size_bytes: number | null;
  // Lección de LicitAI: el nombre del campo de estado DEBE coincidir con lo
  // que serializa el backend (aquí `estado`, el campo SQLAlchemy). Un alias
  // inventado en el front rompía el polling: el campo llegaba `undefined`,
  // nunca se veía COMPLETADO/ERROR y el refetchInterval no paraba.
  estado: DocumentEstado;
  // Float 0.0-1.0 (no porcentaje). El frontend lo multiplica por 100 para
  // mostrarlo en la UI; tratarlo como porcentaje directo causa "0.7%" en
  // lugar de "70%".
  progress: number;
  processing_error: string | null;
  num_chunks: number | null;
  email_meta?: DocumentEmailMeta | null;
  created_at: string;
}

export interface DocumentDetailOut extends DocumentOut {
  texto_extraido?: string | null;
  num_pages?: number | null;
  mime_type?: string | null;
  project_id?: string;
}

export interface DocumentStatusOut {
  id: string;
  // Mismos literales que `DocumentOut.estado` (ver nota arriba).
  estado: DocumentEstado;
  // Fracción 0.0-1.0 — multiplicar por 100 para mostrar.
  progress: number;
  processing_error: string | null;
  num_chunks: number | null;
}

export interface DocumentListOut {
  items: DocumentOut[];
  total: number;
}

export interface UploadedDocumentOut {
  id: string;
  filename: string;
  estado: DocumentEstado;
}

export const TERMINAL_ESTADOS: DocumentEstado[] = [
  "COMPLETADO",
  "ERROR",
  "DUPLICADO",
];

export function isInFlight(estado: DocumentEstado): boolean {
  return !TERMINAL_ESTADOS.includes(estado);
}

// ============================================================================
// Query Keys
// ============================================================================

export const documentKeys = {
  all: ["documents"] as const,
  list: (projectId: string, params?: Record<string, string | number>) =>
    [...documentKeys.all, "list", projectId, params ?? {}] as const,
  detail: (documentId: string) =>
    [...documentKeys.all, "detail", documentId] as const,
  status: (projectId: string, ids: string[]) =>
    [...documentKeys.all, "status", projectId, ids.join(",")] as const,
};

// ============================================================================
// Hooks
// ============================================================================

export interface DocumentListParams {
  estado?: string;
  q?: string;
  page?: number;
  size?: number;
}

export function useDocuments(projectId: string, params: DocumentListParams = {}) {
  const search = new URLSearchParams();
  if (params.estado) search.set("estado", params.estado);
  if (params.q) search.set("q", params.q);
  search.set("page", String(params.page ?? 1));
  search.set("size", String(params.size ?? 20));

  return useQuery<DocumentListOut, Error>({
    queryKey: documentKeys.list(projectId, {
      estado: params.estado ?? "",
      q: params.q ?? "",
      page: params.page ?? 1,
      size: params.size ?? 20,
    }),
    queryFn: () =>
      fetchWithAuth(`/api/v1/projects/${projectId}/documents?${search}`),
    enabled: !!projectId,
    placeholderData: (prev) => prev,
  });
}

/**
 * Polling BATCHED del estado de procesamiento. Una sola request cada 2s
 * para todos los documentos en vuelo; se detiene sola cuando todos
 * alcanzan COMPLETADO/ERROR/DUPLICADO (o la lista de ids queda vacía).
 */
export function useDocumentsStatus(projectId: string, ids: string[]) {
  return useQuery<DocumentStatusOut[], Error>({
    queryKey: documentKeys.status(projectId, ids),
    queryFn: () =>
      fetchWithAuth(
        `/api/v1/projects/${projectId}/documents/status?ids=${ids.join(",")}`
      ),
    enabled: !!projectId && ids.length > 0,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && data.every((d) => !isInFlight(d.estado))) return false;
      return 2000;
    },
    staleTime: 0,
    gcTime: 0,
  });
}

export function useDocumentDetail(documentId: string, enabled = true) {
  return useQuery<DocumentDetailOut, Error>({
    queryKey: documentKeys.detail(documentId),
    // El texto extraído vive en /text (el endpoint /{id} devuelve DocumentOut
    // sin texto_extraido). El visor necesita /text.
    queryFn: () => fetchWithAuth(`/api/v1/documents/${documentId}/text`),
    enabled: enabled && !!documentId,
  });
}

/**
 * Sube UN fichero (multipart field `files`; el endpoint acepta 1..N y
 * devuelve un array — aquí mandamos de uno en uno para tener estado y
 * reintento por fichero; la concurrencia la limita el semáforo del caller).
 */
export async function uploadDocument(
  projectId: string,
  file: File
): Promise<UploadedDocumentOut> {
  const formData = new FormData();
  formData.append("files", file);
  const result: UploadedDocumentOut[] = await fetchWithAuth(
    `/api/v1/projects/${projectId}/documents`,
    { method: "POST", body: formData }
  );
  if (!Array.isArray(result) || result.length === 0) {
    throw new Error("Respuesta de subida inesperada");
  }
  return result[0];
}

export function useDeleteDocument(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (documentId) =>
      fetchWithAuth(`/api/v1/documents/${documentId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.all, "list", projectId],
      });
      showToast.success("Documento eliminado");
    },
    onError: (e) =>
      showToast.error(e.message || "Error al eliminar el documento"),
  });
}

export function useReprocessDocument(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (documentId) =>
      fetchWithAuth(`/api/v1/documents/${documentId}/reprocess`, {
        method: "POST",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.all, "list", projectId],
      });
      showToast.success("Reprocesamiento iniciado");
    },
    onError: (e) =>
      showToast.error(e.message || "Error al reprocesar el documento"),
  });
}
